import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import SearchEmpty from "@images/empty/search_empty.svg"
import SubscribersEmpty from "@images/empty/subscribers_empty.svg"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { ProgramInfoSasPendingSubscribers } from "@modules/programs/programSas/components/programInfoSasPendingSubscribers"
import { ProgramSasLayout } from "@modules/programs/programSas/components/programSasLayout"
import { useProgramSasRequestTotal } from "@modules/programs/programSas/hooks/useProgramSasRequestTotal"
import { ProgramSubscriberItem } from "@modules/programs/programSubscriber/components/programSubscriberItem"
import { useProgramSubscribers } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { ProgramType } from "@modules/programs/types/programTypes"
import { getSubscriptionCountText } from "@modules/programs/utils/programUtils"
import { EmptyView } from "@modules/ui/components/emptyView"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { Search, X } from "lucide-react"
import { Suspense, useCallback, useEffect, useRef, useState } from "react"

type ProgramSubscribersListForOnOffAndInfiniteProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscribersListForOnOffAndInfinite({
  programId,
  className,
  ...props
}: ProgramSubscribersListForOnOffAndInfiniteProps) {
  const [isSASRequestToAcceptViewDisplayed, setIsSASRequestToAcceptViewDisplayed] = useState(false)
  const { data: program } = useProgram(programId)
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const [searchTerms, setsearchTerms] = useState<string>("")
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const debouncedSearchTerms = useDebounce(searchTerms, 300)
  const { data: sasRequestTotal } = useProgramSasRequestTotal(programId)
  const {
    data: { content: subscribers, totalSize },
    isLoading: isLoadingSubscribers,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProgramSubscribers(programId, debouncedSearchTerms)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <div className={cn("flex w-full flex-col px-5 pt-16", className)} {...props}>
        {!isSASRequestToAcceptViewDisplayed ? (
          <>
            {sasRequestTotal.allPending > 0 && program.entranceCheckEnabled && (
              <ProgramInfoSasPendingSubscribers
                programId={program.id}
                displaySASRequestToAcceptView={() => setIsSASRequestToAcceptViewDisplayed(true)}
              />
            )}
            <Text fontStyle="BS" className="mb-2 text-textDefault-body">
              {getSubscriptionCountText(totalSize, isAuthor, isAcceptedSharedCoach, true, program.type).toUpperCase()}
            </Text>
            <InputGroup className="mb-6">
              <InputGroupInput
                placeholder={word("program.subscribersList.search.placeholder")}
                onChange={(e) => setsearchTerms(e.currentTarget.value)}
                value={searchTerms}
              />
              <InputGroupAddon align="inline-start">
                <Search />
              </InputGroupAddon>
              {searchTerms && (
                <InputGroupAddon align="inline-end">
                  <X className="size-4 cursor-pointer" onClick={() => setsearchTerms("")} />
                </InputGroupAddon>
              )}
            </InputGroup>

            {isLoadingSubscribers ? <LoadingSpinner /> : null}

            {subscribers.length > 0 ? (
              <div className="flex flex-col">
                {subscribers.map((subscriber) => (
                  <ProgramSubscriberItem
                    program={program}
                    subscriber={subscriber}
                    key={subscriber.id}
                    canSeeSubscriberDetails={true}
                  />
                ))}
                {(hasNextPage || isFetchingNextPage) && (
                  <div ref={loadMoreRef} className="flex w-full items-center justify-center">
                    <Text fontStyle="BM">{word("global.loading")}</Text>
                  </div>
                )}
              </div>
            ) : !isLoadingSubscribers && !isFetching && searchTerms.length > 0 ? (
              <EmptyView
                image={{ src: SearchEmpty }}
                title={word("explore.search.no_result.title", { searchTerms })}
                description={word("explore.search.no_result.message")}
                flexGrow={false}
                textAlign="left"
                imagePosition="after"
                className="flex w-full bg-surface-background p-0"
              />
            ) : !isLoadingSubscribers && debouncedSearchTerms?.length === 0 ? (
              <EmptyView
                image={{ src: SubscribersEmpty }}
                title={
                  program.type === ProgramType.ONOFF
                    ? word("program.onOff.subscribers.title.empty")
                    : word("program.subscribers.title.empty")
                }
                description={word("program.subscribers.description.empty")}
                flexGrow={false}
                className="bg-surface-background p-6"
              />
            ) : null}
          </>
        ) : (
          <ProgramSasLayout
            programId={programId}
            hideProgramSasLayout={() => setIsSASRequestToAcceptViewDisplayed(false)}
          />
        )}
      </div>
    </Suspense>
  )
}
